import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Button as MuiButton,
  Typography,
  Popover
} from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { InputType } from 'core/enum/inputType';
import useLocales from 'hooks/useLocales';
import { useAIOperationCosts } from '../../../hooks/useAIOperationCosts';
import { useAICreditAvailability } from 'hooks/useAICreditAvailability';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import SingleSelect from '../SelectSingle';
import Button from 'components/Button/Index';
import RangeButton from '../RangeButton/Index';
import ChartComponent from 'components/RadarChart/Index';
import colors from 'assets/variables/colors';

import './index.scss';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    popover: {
      pointerEvents: 'none'
    },
    paper: {
      padding: theme.spacing(2),
      maxWidth: '400px'
    }
  })
);

const MouseOverPopover = ({ children, popUpText }: any) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        aria-owns={open ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        sx={{
          display: 'inline-block',
          marginRight: '10px',
          marginBottom: '9px'
        }}
      >
        {children}
      </Box>
      <Popover
        id="mouse-over-popover"
        className={classes.popover}
        classes={{ paper: classes.paper }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Typography color="GrayText" variant="caption">
          {popUpText}
        </Typography>
      </Popover>
    </>
  );
};

const CompetenciesSelect = ({
  competencies,
  selectedList,
  onSelect,
  setFormValues,
  formValues,
  matchProfile,
  fromCulture,
  setQuery,
  step,
  setStep,
  scores,
  setScores,
  aiUrl,
  setAiUrl,
  onGenerateFromUrl,
  isGeneratingFromUrl,
  showAiUrlInput,
  onGenerateFromTitle,
  isGeneratingFromTitle,
  showAiTitleButton,
  aiTitleCreditCost,
  isAiTitleCreditLimited,
  aiTitleCreditTooltip
}: any) => {
  const { t } = useLocales();
  const { cultureProfileCost } = useAIOperationCosts();
  const { isCreditLimited, outOfCreditsTooltip } = useAICreditAvailability(
    showAiUrlInput ? cultureProfileCost : 0
  );

  useEffect(() => {
    if (step === 1) {
      setScores((prev: any) => {
        const newScores: { [label: string]: number } = {};
        selectedList.forEach((label: string) => {
          newScores[label] = prev[label] ?? 5;
        });
        return newScores;
      });
    }
  }, [selectedList, step, setScores]);

  const handleInputChange = (fieldName: string, value: any) => {
    setFormValues({ ...formValues, [fieldName]: value });
  };
  const { matchSearch } = useSelector((state: RootState) => state.match);

  const handleScoreChange = ({
    label,
    value
  }: {
    label: string;
    value?: number;
  }) => {
    setScores((prev: any) => ({ ...prev, [label]: value ?? 5 }));
  };

  return (
    <Box sx={{ mt: 2 }} className="scrollable">
      {step === 1 && (
        <>
          {fromCulture ? (
            <>
              <div className="field many">
                <span className="label">{t('Culture profile title')}</span>
                <input
                  className="input"
                  type={InputType.text}
                  placeholder={t('Text')}
                  value={formValues.matching}
                  onChange={(e) => handleInputChange('matching', e.target.value)}
                />
              </div>
              {showAiUrlInput && (
                <div className="field many culture-profile-ai">
                  <div className="culture-profile-ai__label-row">
                    <span className="label culture-profile-ai__label">
                      {t('Get competencies based on specified URL')}
                    </span>
                    <span className="culture-profile-ai__tag culture-profile-ai__tag--new">
                      {t('New')}
                    </span>
                    <span className="culture-profile-ai__tag culture-profile-ai__tag--ai">
                      {t('AI')}
                    </span>
                  </div>
                  <p className="culture-profile-ai__credit-hint">
                    {t(
                      `This will use ${cultureProfileCost} AI credits.`
                    )}
                  </p>
                  <div className="culture-profile-ai__url-row">
                    <input
                      className="input"
                      type={InputType.text}
                      placeholder={t('https://example.com')}
                      value={aiUrl}
                      onChange={(e) => setAiUrl(e.target.value)}
                      disabled={isGeneratingFromUrl}
                    />
                    <Button
                      className="culture-profile-ai__go-btn"
                      t={t}
                      title={t('Go')}
                      onClick={onGenerateFromUrl}
                      disabled={
                        isGeneratingFromUrl ||
                        !aiUrl?.trim() ||
                        isCreditLimited
                      }
                      tooltip={
                        isCreditLimited ? outOfCreditsTooltip : undefined
                      }
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            matchProfile && (
              <div className="fields">
                <div className="field many">
                  <span className="label">{t('Matching profile title')}</span>
                  <input
                    className="input"
                    type={InputType.text}
                    placeholder={t('Text')}
                    value={formValues.matching}
                    onChange={(e) =>
                      handleInputChange('matching', e.target.value)
                    }
                  />
                  {showAiTitleButton && (
                    <div className="match-profile-ai">
                      <p className="match-profile-ai__credit-hint">
                        {t(`This will use ${aiTitleCreditCost} AI credits.`)}
                      </p>
                      <Button
                        className="match-profile-ai__button"
                        t={t}
                        title={t('AI Competencies')}
                        onClick={onGenerateFromTitle}
                        isLoading={isGeneratingFromTitle}
                        disabled={
                          isGeneratingFromTitle ||
                          !formValues.matching?.trim() ||
                          isAiTitleCreditLimited
                        }
                        tooltip={aiTitleCreditTooltip}
                      />
                    </div>
                  )}
                </div>
                <div className="field many autocomplete">
                  <span className="label">{t('Select culture profile')}</span>
                  <SingleSelect
                    onInputChange={setQuery}
                    onChange={(e: any) => {
                      if (typeof e != 'string') {
                        handleInputChange('culture_profile', e);
                      }
                    }}
                    onBlur={(e: any) => {
                      if (!matchSearch?.length) {
                        handleInputChange('culture_profile', null);
                      }
                    }}
                    options={matchSearch}
                    show_location={false}
                    placeholder={''}
                    value={formValues.culture_profile}
                  />
                </div>
              </div>
            )
          )}
          {isGeneratingFromUrl && (
            <p className="culture-profile-ai__generating-status">
              {t('Generating')}
              <span
                className="culture-profile-ai__generating-dots"
                aria-hidden="true"
              >
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </p>
          )}
          <div
            className={`culture-profile-ai__competencies${
              isGeneratingFromUrl
                ? ' culture-profile-ai__competencies--generating'
                : ''
            }`}
          >
            {Array.isArray(competencies) &&
              competencies.length &&
              competencies.map((group: any) => (
                <Box key={group.id} sx={{ mt: 2 }}>
                  <Card
                    sx={{
                      p: '0',
                      boxShadow: 'none'
                    }}
                  >
                    <Typography
                      sx={{
                        m: 1,
                        ml: '0',
                        mr: '0',
                        fontFamily: 'Rubik',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        fontSize: '20px',
                        color: '#131417',
                        textAlign: 'center'
                      }}
                    >
                      {t(group?.name)}
                    </Typography>
                    {group?.competencies?.map((competency: any) => {
                      const inArray = selectedList?.includes(competency.name);
                      return (
                        <MouseOverPopover
                          key={competency.id}
                          popUpText={t(competency.description)}
                        >
                          <MuiButton
                            sx={
                              inArray
                                ? {
                                    width: '100%',
                                    color: '#fff',
                                    height: '36px',
                                    fontWeight: '700',
                                    borderRadius: '30px',
                                    fontSize: '14px',
                                    background: colors.tenantColor,
                                    fontFamily: 'Rubik',
                                    letterSpacing: 0,
                                    textTransform: 'none',
                                    '&:hover': {
                                      background: '#CCC7D8',
                                      color: colors.tenantColor
                                    }
                                  }
                                : {
                                    width: '100%',
                                    color: colors.tenantColor,
                                    background: '#EEEAF3',
                                    height: '36px',
                                    fontWeight: '700',
                                    borderRadius: '30px',
                                    fontSize: '14px',
                                    fontFamily: 'Rubik',
                                    letterSpacing: 0,
                                    textTransform: 'none'
                                  }
                            }
                            onClick={() =>
                              onSelect(
                                competency.name,
                                competency.id,
                                competency.description
                              )
                            }
                          >
                            {t(competency.name)}
                          </MuiButton>
                        </MouseOverPopover>
                      );
                    })}
                  </Card>
                </Box>
              ))}
          </div>
        </>
      )}
      {step === 2 && (
        <>
          <Typography sx={{ fontWeight: 600, fontSize: '18px', mb: 2 }}>
            {t('Set minimum score for each competency')}
          </Typography>
          <div className="competencies-flex-step2">
            <div className="sliders">
              {selectedList.map((label: string) => (
                <Box key={label} sx={{ mb: 2 }}>
                  <RangeButton
                    rangeButton={{ label, value: scores[label] ?? 5 }}
                    minValue={1}
                    maxValue={9}
                    onChange={handleScoreChange}
                    values={scores[label] ?? 5}
                  />
                </Box>
              ))}
            </div>
            <div className="radar">
              <ChartComponent
                width={800}
                height={800}
                data={{
                  labels: selectedList.map((label: string) => label),
                  datasets: [
                    {
                      label: '',
                      data: selectedList.map(
                        (label: string) => scores[label] ?? 5
                      ),
                      backgroundColor: 'rgba(0, 158, 226, 0.50)',
                      borderColor: '#009EE2',
                      borderWidth: 1
                    }
                  ]
                }}
              />
            </div>
          </div>
        </>
      )}
    </Box>
  );
};

export function getCompetenciesSelectFooter({
  step,
  t,
  handleBack,
  handleSave,
  handleNext,
  selectedList,
  disabled
}: any) {
  const disabledCondition =
    disabled || selectedList.length < 5 || selectedList.length > 10;

  if (step === 2) {
    return (
      <div className="buttonContainer">
        <Button
          className="back button"
          t={t}
          title={t('Select competencies')}
          onClick={handleBack}
        />
        <Button
          className="next button"
          t={t}
          title={t('Save')}
          onClick={handleSave}
        />
      </div>
    );
  }
  if (step === 1) {
    return (
      <div className="buttonContainer">
        <Button
          className="next button"
          t={t}
          title={t('Next')}
          onClick={handleNext}
          disabled={disabledCondition}
        />
      </div>
    );
  }
  return null;
}

export default CompetenciesSelect;
